import { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SwapHorizontalIcon } from '@app/components/icons';
import TopologyGraphRenderer from '@app/components/topology/TopologyGraphRenderer';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useTopologyApi } from '@app/hooks/useApis';
import type {
  TopologyOverlayPatchschema,
  TopologyPathEndpointSearchRequest,
  TopologyPathEndpointSearchResponse,
  TopologyPathTraceRequest,
  TopologyPathTraceResponse,
  TopologyV2Graphschema
} from '@app/types/sdk';
import {
  TopologyPathEndpointSearchRequestSideEnum,
  TopologyPathEndpointSearchRequestZeroStateModeEnum,
  TopologyPathModeValues,
  TopologyPathRequestContextschemaLaunchSurfaceEnum,
  TopologyPathTraceGoal,
  TopologyV2ScopeTypeschema
} from '@app/types/sdk';
import { resolveErrorMessage } from '@app/utils/errors';
import { replaceOwnedSearchParams } from '@app/utils/search-params';
import { emptyTopologyOverlayState, applyTopologyOverlayPatch, deriveTopologyOverlayPresentation } from '@app/utils/topologyOverlay';

type SearchSide = TopologyPathEndpointSearchRequest['side'];
type TimeMode = 'live' | 'fixed';
type PathTraceStatus = TopologyPathTraceResponse['status'];
type PathEndpointReference = TopologyPathTraceRequest['source'];
type PathTraceEvidenceLevel = NonNullable<TopologyPathTraceRequest['desiredEvidenceLevel']>;
type PathEndpointSearchItem = TopologyPathEndpointSearchResponse['items'][number];
type PathEndpointSearchCoverage = NonNullable<TopologyPathEndpointSearchResponse['coverage']>;
type PathTraceChoice = NonNullable<TopologyPathTraceResponse['choices']>[number];

type TraceSubmissionOverrides = {
  sourceValue?: string;
  sourceReference?: PathEndpointReference | null;
  destinationValue?: string;
  destinationReference?: PathEndpointReference | null;
};

interface PathEndpointFieldProps {
  side: SearchSide;
  label: string;
  placeholder: string;
  value: string;
  selectedReference: PathEndpointReference | null;
  asOf: Date | null;
  disabled?: boolean;
  onChange: (nextValue: string, nextReference: PathEndpointReference | null) => void;
  searchPathEndpoints: (
    request: TopologyPathEndpointSearchRequest,
    signal: AbortSignal
  ) => Promise<TopologyPathEndpointSearchResponse>;
}

const SEARCH_LIMIT = 12;
const PATH_TRACER_SEARCH_PARAM_KEYS = ['source', 'destination', 'asOf', 'autorun'] as const;

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException ? error.name === 'AbortError' : error instanceof Error ? error.name === 'AbortError' : false;

const formatDateTimeLocalInput = (value?: string | Date | null): string => {
  if (!value) {
    return '';
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (num: number) => num.toString().padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const toIsoString = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const toDateValue = (value: string): Date | null => {
  const iso = toIsoString(value);
  return iso ? new Date(iso) : null;
};

const toTopologyScopeType = (value: unknown): TopologyV2ScopeTypeschema | null => {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return (Object.values(TopologyV2ScopeTypeschema) as string[]).includes(normalized) ? (normalized as TopologyV2ScopeTypeschema) : null;
};

const dateLikeToIso = (value: unknown): string | undefined => {
  if (!value) {
    return undefined;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
  }
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
  }
  return undefined;
};

const looksLikeIpv4 = (value: string): boolean => /^\d{1,3}(?:\.\d{1,3}){3}$/.test(value);

const looksLikeIpv6 = (value: string): boolean => value.includes(':') && /^[0-9a-f:]+$/i.test(value);

const looksLikeIpAddress = (value: string): boolean => looksLikeIpv4(value) || looksLikeIpv6(value);

const looksLikePrefix = (value: string): boolean => {
  const slash = value.lastIndexOf('/');
  if (slash <= 0 || slash === value.length - 1) {
    return false;
  }
  const address = value.slice(0, slash).trim();
  const prefix = Number(value.slice(slash + 1));
  return Number.isInteger(prefix) && prefix >= 0 && prefix <= 128 && looksLikeIpAddress(address);
};

const looksLikeMac = (value: string): boolean =>
  /^(?:[0-9a-f]{2}[:-]){5}[0-9a-f]{2}$/i.test(value) || /^(?:[0-9a-f]{4}\.){2}[0-9a-f]{4}$/i.test(value);

const stripDecoratedLiteralSuffix = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  const match = trimmed.match(/^(.*?)(?:\s+)(ip|mac|subnet|prefix)$/i);
  if (!match) {
    return trimmed;
  }
  const literal = match[1]?.trim() ?? '';
  const suffix = match[2]?.trim().toLowerCase() ?? '';
  if (!literal) {
    return trimmed;
  }
  if (suffix === 'ip' && looksLikeIpAddress(literal)) {
    return literal;
  }
  if ((suffix === 'subnet' || suffix === 'prefix') && looksLikePrefix(literal)) {
    return literal;
  }
  if (suffix === 'mac' && looksLikeMac(literal)) {
    return literal;
  }
  return trimmed;
};

const qualifierStringValue = (reference: PathEndpointReference | null | undefined, ...keys: string[]): string | undefined => {
  const qualifiers = reference?.qualifiers as Record<string, any> | undefined;
  if (!qualifiers) {
    return undefined;
  }
  for (const key of keys) {
    const value = qualifiers[key];
    if (value && typeof value === 'object' && typeof value.stringValue === 'string' && value.stringValue.trim()) {
      return value.stringValue.trim();
    }
  }
  return undefined;
};

const literalReferenceValue = (reference: PathEndpointReference | null | undefined): { kind: NonNullable<PathEndpointReference['kind']>; text: string } | null => {
  const ip = qualifierStringValue(reference, 'ip', 'ipAddress', 'ip_address');
  if (ip && looksLikeIpAddress(ip)) {
    return { kind: 'ip_address', text: ip };
  }
  const prefix = qualifierStringValue(reference, 'prefix', 'subnet');
  if (prefix && looksLikePrefix(prefix)) {
    return { kind: 'subnet', text: prefix };
  }
  const mac = qualifierStringValue(reference, 'mac', 'macAddress', 'mac_address');
  if (mac && looksLikeMac(mac)) {
    return { kind: 'mac_address', text: mac };
  }
  return null;
};

const canonicalizePathTraceReference = (reference: PathEndpointReference): PathEndpointReference => {
  const literal = literalReferenceValue(reference);
  if (literal) {
    return {
      ...reference,
      kind: literal.kind,
      text: literal.text
    };
  }
  const normalizedText = stripDecoratedLiteralSuffix(reference.text?.trim() ?? '');
  if (normalizedText && normalizedText !== (reference.text?.trim() ?? '')) {
    return {
      ...reference,
      text: normalizedText
    };
  }
  return reference;
};

const pathEndpointInputValue = (reference: PathEndpointReference | null | undefined, fallbackDisplay = ''): string => {
  const literal = literalReferenceValue(reference);
  if (literal) {
    return literal.text;
  }
  const text = stripDecoratedLiteralSuffix(reference?.text?.trim() ?? '');
  if (text) {
    return text;
  }
  const displayName = stripDecoratedLiteralSuffix(reference?.displayName?.trim() ?? '');
  if (displayName) {
    return displayName;
  }
  return stripDecoratedLiteralSuffix(fallbackDisplay);
};

const topologyPathTracePatchHasEmbeddedGraph = (patch: TopologyOverlayPatchschema | null | undefined): boolean => {
  if (!patch) {
    return false;
  }
  return (patch.ops ?? []).some((op: any) => {
    if (op?.op === 'upsert_layer' && op.layer?.graphOverlay?.graph) {
      return true;
    }
    if (op?.op === 'upsert_graph_overlay' && op.graphOverlay?.graph) {
      return true;
    }
    return false;
  });
};

const buildPathTraceGraph = (response: TopologyPathTraceResponse): TopologyV2Graphschema | null => {
  const patch = response.patch;
  const base = patch?.base;
  const viewId = typeof base?.viewId === 'string' ? base.viewId.trim() : '';
  const scopeType = toTopologyScopeType(base?.scope?.scopeType);
  const scopeId = typeof base?.scope?.scopeId === 'string' ? base.scope.scopeId.trim() : '';
  const timeMode = base?.timeMode === 'fixed' ? 'fixed' : base?.timeMode === 'current' ? 'current' : '';

  if (!patch || !base || !viewId || !scopeType || !scopeId || !timeMode || !topologyPathTracePatchHasEmbeddedGraph(patch)) {
    return null;
  }

  // Standalone paths:trace responses carry a self-contained related-view graph inside the overlay patch,
  // so the page can render directly from that payload without resolving a base topology view.
  const runtimeState = applyTopologyOverlayPatch(emptyTopologyOverlayState(base), patch, { revision: 1 });
  const placeholderGraph: TopologyV2Graphschema = {
    graphId: `path-trace:${viewId}:${scopeType}:${scopeId}:${dateLikeToIso(base.asOf) ?? timeMode}`,
    generatedAt: new Date(),
    scope: {
      scopeType,
      scopeId
    },
    nodes: [],
    edges: [],
    meta: {
      sourceViewId: viewId
    }
  };

  return deriveTopologyOverlayPresentation(placeholderGraph, runtimeState).graph;
};

const responseTone = (status: PathTraceStatus): 'success' | 'warning' | 'danger' | 'ghost' => {
  switch (status) {
    case 'ready':
      return 'success';
    case 'needs_clarification':
      return 'warning';
    case 'not_found':
    case 'unsupported':
    case 'unavailable':
      return 'danger';
    default:
      return 'ghost';
  }
};

const extractClarificationChoiceReference = (choice: PathTraceChoice): PathEndpointReference | null => {
  const rawValue = choice.value;
  const rawReference =
    rawValue && typeof rawValue === 'object'
      ? rawValue.reference ??
        rawValue.endpointReference ??
        rawValue.pathEndpointReference ??
        rawValue.endpoint ??
        rawValue.selector ??
        rawValue
      : null;

  if (!rawReference || typeof rawReference !== 'object') {
    return choice.label.trim() ? { text: choice.label.trim() } : null;
  }

  const candidate = rawReference as Record<string, unknown>;
  const reference: PathEndpointReference = {
    ...(typeof candidate.kind === 'string' ? { kind: candidate.kind as PathEndpointReference['kind'] } : {}),
    ...(typeof candidate.id === 'string' ? { id: candidate.id } : {}),
    ...(typeof candidate.displayName === 'string' ? { displayName: candidate.displayName } : {}),
    ...(typeof candidate.text === 'string' ? { text: candidate.text } : {}),
    ...(typeof candidate.semanticEntity === 'string' ? { semanticEntity: candidate.semanticEntity } : {}),
    ...(candidate.catalogIds instanceof Set
      ? { catalogIds: candidate.catalogIds as PathEndpointReference['catalogIds'] }
      : Array.isArray(candidate.catalogIds)
        ? { catalogIds: new Set(candidate.catalogIds.filter((value): value is string => typeof value === 'string')) }
        : {}),
    ...(candidate.qualifiers && typeof candidate.qualifiers === 'object'
      ? { qualifiers: candidate.qualifiers as PathEndpointReference['qualifiers'] }
      : {}),
    ...(typeof candidate.scopeId === 'string' ? { scopeId: candidate.scopeId } : {})
  };

  if (reference.kind || reference.id || reference.displayName || reference.text || reference.semanticEntity || reference.scopeId) {
    return reference;
  }
  return choice.label.trim() ? { text: choice.label.trim() } : null;
};

const PathEndpointField = ({
  side,
  label,
  placeholder,
  value,
  selectedReference,
  asOf,
  disabled = false,
  onChange,
  searchPathEndpoints
}: PathEndpointFieldProps) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<PathEndpointSearchItem[]>([]);
  const [coverage, setCoverage] = useState<PathEndpointSearchCoverage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const query = value.trim();

  useEffect(() => {
    if (!open || disabled) {
      return;
    }

    const controller = new AbortController();
    const delay = query ? 180 : 0;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await searchPathEndpoints(
          query
            ? {
                side,
                query,
                requestedMode: TopologyPathModeValues.Auto,
                allowCrossSiteFallback: true,
                limit: SEARCH_LIMIT,
                ...(asOf ? { asOf } : {}),
                context: {
                  launchSurface: TopologyPathRequestContextschemaLaunchSurfaceEnum.StandalonePage
                }
              }
            : {
                side,
                zeroStateMode: TopologyPathEndpointSearchRequestZeroStateModeEnum.BoundedDefaults,
                requestedMode: TopologyPathModeValues.Auto,
                allowCrossSiteFallback: true,
                limit: SEARCH_LIMIT,
                ...(asOf ? { asOf } : {}),
                context: {
                  launchSurface: TopologyPathRequestContextschemaLaunchSurfaceEnum.StandalonePage
                }
              },
          controller.signal
        );
        if (controller.signal.aborted) {
          return;
        }
        setItems(response.items ?? []);
        setCoverage(response.coverage ?? null);
        setHighlightedIndex(0);
      } catch (searchError) {
        if (controller.signal.aborted || isAbortError(searchError)) {
          return;
        }
        setItems([]);
        setCoverage(null);
        setError(await resolveErrorMessage(searchError, 'Unable to load suggestions.'));
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, delay);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [asOf, disabled, open, query, searchPathEndpoints, side]);

  useEffect(() => {
    if (!selectedReference && value.trim().length === 0) {
      setItems([]);
      setCoverage(null);
      setError(null);
      setHighlightedIndex(0);
    }
  }, [selectedReference, value]);

  const selectItem = useCallback(
    (item: PathEndpointSearchItem) => {
      const reference = canonicalizePathTraceReference(item.reference);
      const displayValue = pathEndpointInputValue(reference, item.displayName?.trim() || value);
      onChange(displayValue, reference);
      setOpen(false);
      setItems([]);
      setCoverage(null);
      setError(null);
      setHighlightedIndex(0);
    },
    [onChange, value]
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (!open) {
        if (event.key === 'ArrowDown' && items.length > 0) {
          event.preventDefault();
          setOpen(true);
        }
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex((current) => (items.length === 0 ? 0 : Math.min(current + 1, items.length - 1)));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex((current) => (items.length === 0 ? 0 : Math.max(current - 1, 0)));
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        return;
      }
      if (event.key === 'Enter' && items[highlightedIndex]) {
        event.preventDefault();
        selectItem(items[highlightedIndex]);
      }
    },
    [highlightedIndex, items, open, selectItem]
  );

  return (
    <label className="path-tracer__field">
      <span className="path-tracer__field-label">{label}</span>
      <div
        ref={rootRef}
        className={`path-tracer__combobox${open ? ' is-open' : ''}${error ? ' is-error' : ''}`}
        onBlur={(event) => {
          const nextTarget = event.relatedTarget as Node | null;
          if (!rootRef.current?.contains(nextTarget)) {
            setOpen(false);
          }
        }}>
        <input
          type="text"
          value={value}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          aria-label={label}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          onChange={(event) => {
            onChange(event.target.value, null);
            setOpen(true);
          }}
        />
        {(open || loading || Boolean(error)) && (
          <div className="path-tracer__menu" role="listbox" aria-label={`${label} suggestions`}>
            {loading && <div className="path-tracer__menu-state">Loading suggestions…</div>}
            {!loading && error && <div className="path-tracer__menu-state path-tracer__menu-state--error">{error}</div>}
            {!loading && !error && items.length === 0 && (
              <div className="path-tracer__menu-state">
                {query ? `No ${side} matches for "${query}".` : 'Suggestions will appear here.'}
              </div>
            )}
            {!loading &&
              !error &&
              items.map((item, index) => {
                const siteLabel = item.site?.name?.trim() || item.site?.id?.trim() || '';
                return (
                  <button
                    key={`${item.kind}:${item.reference.id ?? item.reference.text ?? item.displayName}:${index}`}
                    type="button"
                    className={`path-tracer__menu-item${index === highlightedIndex ? ' is-active' : ''}`}
                    role="option"
                    aria-selected={index === highlightedIndex}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      selectItem(item);
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}>
                    <span className="path-tracer__menu-title">
                      <strong>{item.displayName}</strong>
                      <span>{item.kind.replace(/_/g, ' ')}</span>
                    </span>
                    {(item.summary || siteLabel) && (
                      <span className="path-tracer__menu-summary">
                        {item.summary}
                        {item.summary && siteLabel ? ' • ' : ''}
                        {siteLabel}
                      </span>
                    )}
                  </button>
                );
              })}
            {!loading && !error && coverage?.scopeMode && (
              <div className="path-tracer__menu-footer">Coverage: {coverage.scopeMode.replace(/_/g, ' ')}</div>
            )}
          </div>
        )}
      </div>
    </label>
  );
};

const TopologyPathTracerPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const orgId = useActiveOrgId();
  const topologyApi = useTopologyApi();
  const initialAutorunRef = useRef(searchParams.get('autorun') === '1' || searchParams.get('autorun') === 'true');
  const hasAutorunTriggeredRef = useRef(false);

  const [sourceValue, setSourceValue] = useState(() => stripDecoratedLiteralSuffix(searchParams.get('source') ?? ''));
  const [sourceReference, setSourceReference] = useState<PathEndpointReference | null>(null);
  const [destinationValue, setDestinationValue] = useState(() => stripDecoratedLiteralSuffix(searchParams.get('destination') ?? ''));
  const [destinationReference, setDestinationReference] = useState<PathEndpointReference | null>(null);
  const [destinationPort, setDestinationPort] = useState('');
  const [protocol, setProtocol] = useState('');
  const [desiredEvidenceLevel, setDesiredEvidenceLevel] = useState<PathTraceEvidenceLevel | ''>('');
  const [timeMode, setTimeMode] = useState<TimeMode>(() => (searchParams.get('asOf') ? 'fixed' : 'live'));
  const [asOfInput, setAsOfInput] = useState(() => formatDateTimeLocalInput(searchParams.get('asOf')));
  const [traceResponse, setTraceResponse] = useState<TopologyPathTraceResponse | null>(null);
  const [traceGraph, setTraceGraph] = useState<TopologyV2Graphschema | null>(null);
  const [traceGraphVersion, setTraceGraphVersion] = useState(0);
  const [traceError, setTraceError] = useState<string | null>(null);
  const [tracing, setTracing] = useState(false);
  const autorunEnabled = initialAutorunRef.current;

  useEffect(() => {
    const source = sourceReference ? pathEndpointInputValue(sourceReference, sourceValue) : stripDecoratedLiteralSuffix(sourceValue);
    const destination = destinationReference ? pathEndpointInputValue(destinationReference, destinationValue) : stripDecoratedLiteralSuffix(destinationValue);
    const asOf = timeMode === 'fixed' ? toIsoString(asOfInput) : null;
    const next = replaceOwnedSearchParams(searchParams, PATH_TRACER_SEARCH_PARAM_KEYS, {
      source,
      destination,
      asOf
    });
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [asOfInput, destinationValue, searchParams, setSearchParams, sourceValue, timeMode]);

  const searchPathEndpoints = useCallback(
    async (request: TopologyPathEndpointSearchRequest, signal: AbortSignal): Promise<TopologyPathEndpointSearchResponse> => {
      if (!orgId) {
        throw new Error('No active organization is selected.');
      }
      return topologyApi.searchTopologyPathEndpoints(
        {
          orgId,
          topologyPathEndpointSearchRequest: request
        },
        { signal }
      );
    },
    [orgId, topologyApi]
  );

  const handleSourceChange = useCallback((nextValue: string, nextReference: PathEndpointReference | null) => {
    setSourceValue(nextValue);
    setSourceReference(nextReference);
  }, []);

  const handleDestinationChange = useCallback((nextValue: string, nextReference: PathEndpointReference | null) => {
    setDestinationValue(nextValue);
    setDestinationReference(nextReference);
  }, []);

  const submitTrace = useCallback(async (overrides?: TraceSubmissionOverrides) => {
    if (!orgId) {
      setTraceError('No active organization is selected.');
      return;
    }
    const effectiveSourceValue = overrides?.sourceValue ?? sourceValue;
    const effectiveSourceReference = overrides && 'sourceReference' in overrides ? (overrides.sourceReference ?? null) : sourceReference;
    const effectiveDestinationValue = overrides?.destinationValue ?? destinationValue;
    const effectiveDestinationReference =
      overrides && 'destinationReference' in overrides ? (overrides.destinationReference ?? null) : destinationReference;
    const normalizedSourceReference = effectiveSourceReference ? canonicalizePathTraceReference(effectiveSourceReference) : null;
    const normalizedDestinationReference = effectiveDestinationReference ? canonicalizePathTraceReference(effectiveDestinationReference) : null;
    const source = normalizedSourceReference ? pathEndpointInputValue(normalizedSourceReference, effectiveSourceValue) : stripDecoratedLiteralSuffix(effectiveSourceValue);
    const destination = normalizedDestinationReference
      ? pathEndpointInputValue(normalizedDestinationReference, effectiveDestinationValue)
      : stripDecoratedLiteralSuffix(effectiveDestinationValue);
    if (!source || !destination) {
      setTraceError('Source and destination are required.');
      return;
    }

    const asOf = timeMode === 'fixed' ? toDateValue(asOfInput) : null;
    if (timeMode === 'fixed' && !asOf) {
      setTraceError('Choose a valid "As of" timestamp or switch back to Live.');
      return;
    }

    const parsedDestinationPort = destinationPort.trim() === '' ? undefined : Number(destinationPort);
    if (
      parsedDestinationPort !== undefined &&
      (!Number.isFinite(parsedDestinationPort) || !Number.isInteger(parsedDestinationPort) || parsedDestinationPort < 0 || parsedDestinationPort > 65535)
    ) {
      setTraceError('Destination port must be a whole number between 0 and 65535.');
      return;
    }

    const trafficSelector =
      protocol || parsedDestinationPort !== undefined
        ? {
            ...(protocol ? { protocol } : {}),
            ...(parsedDestinationPort !== undefined ? { destinationPort: parsedDestinationPort } : {})
          }
        : undefined;

    setTracing(true);
    setTraceError(null);
    try {
      const request: TopologyPathTraceRequest = {
        source: normalizedSourceReference ?? { text: source },
        destination: normalizedDestinationReference ?? { text: destination },
        requestedMode: TopologyPathModeValues.Auto,
        goal: TopologyPathTraceGoal.Visualize,
        context: {
          launchSurface: TopologyPathRequestContextschemaLaunchSurfaceEnum.StandalonePage
        },
        ...(trafficSelector ? { trafficSelector } : {}),
        ...(desiredEvidenceLevel ? { desiredEvidenceLevel } : {}),
        ...(asOf ? { asOf } : {})
      };
      const response = await topologyApi.traceTopologyPath({
        orgId,
        topologyPathTraceRequest: request
      });
      setTraceResponse(response);
      setTraceGraph(buildPathTraceGraph(response));
      setTraceGraphVersion((current) => current + 1);
    } catch (error) {
      setTraceGraph(null);
      setTraceError(await resolveErrorMessage(error, 'Unable to trace the requested path.'));
    } finally {
      setTracing(false);
    }
  }, [asOfInput, desiredEvidenceLevel, destinationPort, destinationReference, destinationValue, orgId, protocol, sourceReference, sourceValue, timeMode, topologyApi]);

  const handleSwapEndpoints = useCallback(() => {
    const nextSourceValue = destinationValue;
    const nextSourceReference = destinationReference;
    const nextDestinationValue = sourceValue;
    const nextDestinationReference = sourceReference;

    setSourceValue(nextSourceValue);
    setSourceReference(nextSourceReference);
    setDestinationValue(nextDestinationValue);
    setDestinationReference(nextDestinationReference);
    setTraceResponse(null);
    setTraceGraph(null);
    setTraceError(null);

    if (!nextSourceValue.trim() || !nextDestinationValue.trim()) {
      return;
    }

    void submitTrace({
      sourceValue: nextSourceValue,
      sourceReference: nextSourceReference,
      destinationValue: nextDestinationValue,
      destinationReference: nextDestinationReference
    });
  }, [destinationReference, destinationValue, sourceReference, sourceValue, submitTrace]);

  const handleClarificationChoiceSelect = useCallback(
    async (choice: PathTraceChoice) => {
      const extractedReference = extractClarificationChoiceReference(choice);
      const reference = extractedReference ? canonicalizePathTraceReference(extractedReference) : null;
      if (!reference) {
        setTraceError('Unable to apply the selected clarification choice.');
        return;
      }

      const rawValue = choice.value;
      const explicitSide =
        rawValue && typeof rawValue === 'object'
          ? rawValue.side === TopologyPathEndpointSearchRequestSideEnum.Source ||
            rawValue.side === TopologyPathEndpointSearchRequestSideEnum.Destination
            ? rawValue.side
            : rawValue.endpointSide === TopologyPathEndpointSearchRequestSideEnum.Source ||
                rawValue.endpointSide === TopologyPathEndpointSearchRequestSideEnum.Destination
              ? rawValue.endpointSide
              : null
          : null;

      const inferredSide =
        explicitSide ??
        (sourceReference === null && destinationReference !== null
          ? TopologyPathEndpointSearchRequestSideEnum.Source
          : destinationReference === null && sourceReference !== null
            ? TopologyPathEndpointSearchRequestSideEnum.Destination
            : null);

      if (!inferredSide) {
        setTraceError('The selected clarification choice did not identify whether it applies to source or destination.');
        return;
      }

      const displayValue = pathEndpointInputValue(reference, choice.label.trim());

      if (inferredSide === TopologyPathEndpointSearchRequestSideEnum.Source) {
        setSourceValue(displayValue);
        setSourceReference(reference);
        await submitTrace({
          sourceValue: displayValue,
          sourceReference: reference
        });
        return;
      }

      setDestinationValue(displayValue);
      setDestinationReference(reference);
      await submitTrace({
        destinationValue: displayValue,
        destinationReference: reference
      });
    },
    [destinationReference, sourceReference, submitTrace]
  );

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      await submitTrace();
    },
    [submitTrace]
  );

  useEffect(() => {
    if (!autorunEnabled || hasAutorunTriggeredRef.current || tracing) {
      return;
    }
    if (!sourceValue.trim() || !destinationValue.trim()) {
      return;
    }
    if (timeMode === 'fixed' && !toDateValue(asOfInput)) {
      return;
    }
    hasAutorunTriggeredRef.current = true;
    void submitTrace();
  }, [asOfInput, autorunEnabled, destinationValue, sourceValue, submitTrace, timeMode, tracing]);

  const effectiveAsOf = timeMode === 'fixed' ? toDateValue(asOfInput) : null;
  const inlineMessage =
    traceResponse?.question?.trim() ||
    traceResponse?.summary?.trim() ||
    traceResponse?.reason?.trim() ||
    null;
  const readyWithoutGraph = Boolean(traceResponse && traceResponse.status === 'ready' && !traceGraph);
  const showInlineStatus = Boolean(traceResponse && traceResponse.status !== 'ready' && inlineMessage);
  const showClarificationChoices = traceResponse?.status === 'needs_clarification' && (traceResponse.choices?.length ?? 0) > 0;
  const swapDisabled = !orgId || tracing || (sourceValue.trim().length === 0 && destinationValue.trim().length === 0);
  const emptyStateTitle = tracing
    ? 'Tracing the selected path…'
    : traceResponse?.status === 'needs_clarification'
      ? 'Choose one of the suggested matches to continue.'
      : readyWithoutGraph
        ? inlineMessage ?? 'The trace completed, but no renderable graph was returned.'
      : traceResponse?.status && traceResponse.status !== 'ready'
        ? inlineMessage ?? 'The path could not be rendered yet.'
        : 'Pick source and destination, then trace.';
  const emptyStateBody = tracing
    ? 'The page stays in place and will refresh the path canvas as soon as the backend finishes the trace.'
    : traceResponse?.status === 'needs_clarification'
      ? 'The tracer could not safely pick a single site or endpoint from the current input. Use the choices above to continue with the intended scope.'
      : readyWithoutGraph
        ? 'The request was submitted, but the standalone page did not receive an embedded related-view graph to render. This usually points to a backend/path-data issue rather than a missing input.'
      : traceResponse?.status && traceResponse.status !== 'ready'
        ? 'Adjust the inputs above or try a more specific free-form value. Search-backed suggestions still work for partial prefixes such as 10.20.'
        : 'Focus either endpoint field for suggestions.';

  return (
    <article className="path-tracer-page">
      <header className="path-tracer-page__header">
        <h1>Path Tracer</h1>
      </header>

      <form className="path-tracer-page__toolbar" onSubmit={handleSubmit}>
        <PathEndpointField
          side={TopologyPathEndpointSearchRequestSideEnum.Source}
          label="Source"
          placeholder="IP, subnet, hostname, interface, ENI, VPC, tunnel, service…"
          value={sourceValue}
          selectedReference={sourceReference}
          asOf={effectiveAsOf}
          disabled={!orgId}
          onChange={handleSourceChange}
          searchPathEndpoints={searchPathEndpoints}
        />
        <div className="path-tracer__swap">
          <button
            type="button"
            className="icon-button path-tracer__swap-button"
            aria-label="Swap source and destination"
            title="Swap source and destination"
            disabled={swapDisabled}
            onClick={handleSwapEndpoints}>
            <SwapHorizontalIcon size={18} />
          </button>
        </div>
        <PathEndpointField
          side={TopologyPathEndpointSearchRequestSideEnum.Destination}
          label="Destination"
          placeholder="IP, subnet, hostname, interface, ENI, VPC, tunnel, service…"
          value={destinationValue}
          selectedReference={destinationReference}
          asOf={effectiveAsOf}
          disabled={!orgId}
          onChange={handleDestinationChange}
          searchPathEndpoints={searchPathEndpoints}
        />
        <label className="path-tracer__field path-tracer__field--port">
          <span className="path-tracer__field-label">Destination port</span>
          <input
            type="number"
            inputMode="numeric"
            placeholder="e.g. 443 HTTPS, 53 DNS (optional)"
            value={destinationPort}
            onChange={(event) => setDestinationPort(event.target.value)}
            disabled={!orgId || tracing}
          />
        </label>
        <label className="path-tracer__field path-tracer__field--protocol">
          <span className="path-tracer__field-label">Protocol</span>
          <input
            type="number"
            inputMode="numeric"
            placeholder="e.g. 6 TCP, 17 UDP, -1 all (optional)"
            value={protocol}
            onChange={(event) => setProtocol(event.target.value)}
            disabled={!orgId || tracing}
          />
        </label>
        <label className="path-tracer__field path-tracer__field--evidence">
          <span className="path-tracer__field-label">Evidence level</span>
          <select
            value={desiredEvidenceLevel}
            onChange={(event) => setDesiredEvidenceLevel(event.target.value as PathTraceEvidenceLevel | '')}
            disabled={!orgId || tracing}>
            <option value="">Best available</option>
            <option value="forwarding_supported">Forwarding supported</option>
            <option value="policy_evaluated">Policy evaluated</option>
          </select>
        </label>
        <label className="path-tracer__field path-tracer__field--time">
          <span className="path-tracer__field-label">Time</span>
          <div className="path-tracer__time-controls">
            <select value={timeMode} onChange={(event) => setTimeMode(event.target.value === 'fixed' ? 'fixed' : 'live')}>
              <option value="live">Live</option>
              <option value="fixed">As of</option>
            </select>
            {timeMode === 'fixed' && (
              <input
                type="datetime-local"
                value={asOfInput}
                onChange={(event) => setAsOfInput(event.target.value)}
                aria-label="As of timestamp"
              />
            )}
          </div>
        </label>
        <div className="path-tracer__actions">
          <button
            type="submit"
            className="ui-button ui-button--primary"
            disabled={!orgId || tracing || sourceValue.trim().length === 0 || destinationValue.trim().length === 0}>
            {tracing ? 'Tracing…' : 'Trace Path'}
          </button>
        </div>
      </form>

      {traceError && (
        <p className="path-tracer-page__banner path-tracer-page__banner--error" role="alert">
          {traceError}
        </p>
      )}

      {showInlineStatus && (
        <section
          className={`path-tracer-page__status-strip path-tracer-page__status-strip--${traceResponse ? responseTone(traceResponse.status) : 'ghost'}`}
          aria-live="polite">
          {traceResponse && <span className={`ui-badge ui-badge--${responseTone(traceResponse.status)}`}>{traceResponse.status.replace(/_/g, ' ')}</span>}
          {inlineMessage && <p>{inlineMessage}</p>}
        </section>
      )}

      {showClarificationChoices && (
        <section className="path-tracer-page__choices" aria-label="Clarification choices">
          {traceResponse?.choices?.map((choice: PathTraceChoice) => (
            <button
              key={choice.id}
              type="button"
              className="path-tracer-page__choice"
              onClick={() => void handleClarificationChoiceSelect(choice)}>
              <strong>{choice.label}</strong>
              {choice.description && <span>{choice.description}</span>}
            </button>
          ))}
        </section>
      )}

      <section className="path-tracer-page__workspace">
        <div className="path-tracer-page__workspace-scroll">
          {traceGraph ? (
            <div className="path-tracer-page__artifact">
              <TopologyGraphRenderer
                topology={traceGraph}
                graphKey={`${traceGraph?.graphId ?? 'path-trace'}:${traceGraphVersion}`}
                showControls={false}
              />
            </div>
          ) : (
            <div className="path-tracer-page__empty-state">
              <strong>{emptyStateTitle}</strong>
              <p>
                {emptyStateBody}
                {!traceResponse ? (
                  <>
                    {' '}
                    Prefixes like <code>10.20.</code> populate matches, and exact free-form entries still submit directly.
                  </>
                ) : null}
              </p>
            </div>
          )}
        </div>
      </section>
    </article>
  );
};

export default TopologyPathTracerPage;
